import React, { useRef, useState } from "react";
import { X, Loader2, User, Building2, IndianRupee, ImageIcon } from "lucide-react";
import { createPlacementAPI } from "../services/apicollection";

const PlacementForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    studentName: "",
    companyName: "",
    package: "",
  });
  const [profilePreview, setProfilePreview] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please upload a valid image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setProfilePreview(event.target?.result || null);
      setProfileFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.studentName?.trim() || !formData.companyName?.trim()) {
      alert("Student name and company name are required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        studentName: formData.studentName.trim(),
        companyName: formData.companyName.trim(),
        package: formData.package?.trim() || "",
        profileFile,
      };
      const res = await createPlacementAPI(payload);
      if (!res?.status) {
        alert(res?.message || "Failed to create placement");
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message || "Failed to create placement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-200">Add Placement</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Add a student placement to display on the home page and placements page.
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#13233a] text-slate-500 hover:bg-slate-200"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-400">
            Student name <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
            <User size={16} className="text-slate-400" />
            <input
              type="text"
              value={formData.studentName}
              onChange={(e) => handleChange("studentName", e.target.value)}
              className="h-7 w-full border-none bg-transparent text-sm outline-none"
              placeholder="e.g. Rahul Sharma"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">
            Company name <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
            <Building2 size={16} className="text-slate-400" />
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              className="h-7 w-full border-none bg-transparent text-sm outline-none"
              placeholder="e.g. Google"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">
            Package (optional)
          </label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
            <IndianRupee size={16} className="text-slate-400" />
            <input
              type="text"
              value={formData.package}
              onChange={(e) => handleChange("package", e.target.value)}
              className="h-7 w-full border-none bg-transparent text-sm outline-none"
              placeholder="e.g. 12 LPA"
            />
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-400">
            <ImageIcon size={16} className="text-slate-400" />
            Profile photo (optional)
          </label>
          <div className="mt-1 flex flex-col gap-3 rounded-lg border border-dashed border-slate-300 p-4 text-center">
            {profilePreview ? (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={profilePreview}
                  alt="Preview"
                  className="h-24 w-24 rounded-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setProfilePreview(null);
                    setProfileFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="text-xs font-medium text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-400">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="font-semibold text-slate-200 underline hover:no-underline"
                  >
                    Browse files
                  </button>{" "}
                  or drag & drop
                </p>
                <p className="text-[11px] text-slate-400">JPG / PNG up to 5MB</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfileChange}
              className="hidden"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[#1d3350] px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-[#0d1b2e]"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-700 disabled:opacity-60"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            Add Placement
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlacementForm;
